import { useCallback } from 'react';
import useWalletCurrency from '~/client/hooks/currency/useWalletCurrency';
import useQueryParam from '~/client/hooks/router/useQueryParam';
import useRouter from '~/client/hooks/router/useRouter';
import useAgentData from '~/client/hooks/useAgentData';
import useLanguage from '~/client/hooks/useLanguage';
import useSite from '~/client/hooks/useSite';
import useAppContext from '~/shared/context/AppContext/useAppContext';
import getSessionLangPath from './helpers/getSessionLangPath';
import { useStartGameSessionMutation } from './StartGameSessionMutation.generated';
import useGetInGameCurrencyValue from './useGetInGameCurrencyValue';

const useStartGameSession = () => {
  const { site } = useSite();
  const { code: language } = useLanguage();
  const { isMobile } = useAgentData();
  const [startSession] = useStartGameSessionMutation();
  const currency = useWalletCurrency();
  const { getInGameCurrencyValue } = useGetInGameCurrencyValue();
  const { pathPrefix } = useAppContext();
  const { locale } = useRouter();
  const [deeplink] = useQueryParam('deeplink');

  const startGameSession = useCallback(
    async (gameId: string, isFunMode: boolean, inGameCurrency?: string) => {
      const lobbyUrl = getLobbyUrl(locale, pathPrefix);
      const payload = await startSession({
        variables: {
          site,
          gameId,
          currency,
          language,
          isMobile,
          lobbyUrl,
          isFunMode,
          inGameCurrency: getInGameCurrencyValue(inGameCurrency),
          setIframePath: deeplink,
        },
      });
      const session = payload?.data?.casinoStartGameSession;
      return session;
    },
    [
      currency,
      getInGameCurrencyValue,
      isMobile,
      language,
      locale,
      pathPrefix,
      site,
      startSession,
      deeplink,
    ],
  );

  return startGameSession;
};

const getLobbyUrl = (locale: LanguageCode, pathPrefix: string) => {
  const languagePath = getSessionLangPath(locale);
  const basePath = `${languagePath}${pathPrefix}`;
  const base = location.origin.includes('localhost')
    ? 'https://staging.bitcasino.io/'
    : location.origin;
  return new URL(basePath, base).toString();
};

export default useStartGameSession;
